/* prettier-ignore */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RestService } from './rest.service';
import { DataChatService } from 'src/chatbot/service/dataChat.service';
import { ToolsService } from './tools.service';
import { TranslationService } from './translation.service';
import { DebugService } from './debug.service';
import { generateToken } from 'src/chatbot/helpers/helpers';
import { AudioStorageService } from 'src/chatbot/service/audio-storage.service';
import { UserGeolocation } from 'src/app/utils/geolocation';

export interface Statistics{
    clientId: number,
    deviceId: number,
    channelId: number,
    sessionTime: number,
    language: string,
    points: string[],
    events: string[],
    hikings: string[],
    pdfs: string[],
    travelerType: string,
    categories: string,
    subCategories: string,
    deviceType: number,
    clicks: number,
    login: string;
    subdomain?: string;
    chatToken?: string;
    sessionId?: string;
    geolocation?: UserGeolocation | null;
    dinamic_stats?: string | null;
}

@Injectable({
    providedIn: 'root'
})

export class StatisticsService {
    sessionId: any;
    token: any;

    sessionActiveTime: number = 0;
    lastSessionTime: number = new Date().getTime();
    sesh: Statistics;
    sessionTime: number;
    clientId: number;
    deviceId: number;
    channelId: number;
    points: string[] = [];
    events: string[] = [];
    hikings: string[] = [];
    pdfs: string[] = [];
    travelerType: any = null;
    categories: any = null;
    subCategories: any = null;
    clicks: number = 0;
    login: string = '';
    
    constructor(
        public http: HttpClient, 
        public rest: RestService, 
        private dataChat: DataChatService, 
        private debugService: DebugService,
        private tools: ToolsService,
        private audioStorageService: AudioStorageService,
        private _translationService: TranslationService)
    {}

    setLocalStorageSessionIDFromClick() {
        if (!this.sessionId) 
          return;
    
        this.tools.setWithExpiry("sessionId", this.sessionId);
        this.debugService.warn('Saving sessionId localStorage', '[ToolsService]');
    }
    
    setLocalStorageTokenFromClick() {
        if (!this.token) 
          return;
    
        this.tools.setWithExpiry("token", this.token);
        this.debugService.warn('Saving token localStorage', '[ToolsService]');
    }
    
    setSessionId() {
        const session = this.tools.getWithExpiry("sessionId");
        if (!session)
           this.audioStorageService.clearAllAudios();

        this.sessionId = session ?? generateToken(); //`sessionId_${uuidv4()}`;
        this.setLocalStorageSessionIDFromClick();
        this.debugService.warn(session ? `SessionId loaded from localStorage: ${session}`: `New sessionId generated ${session}`, '[ToolsService]');
    }

    /** Always returns a non-empty session id for CMS / Metrics identity (save-route, geo). */
    ensureSessionId(): string {
        const current = String(this.sessionId ?? '').trim();
        if (current) {
            return current;
        }
        this.setSessionId();
        return String(this.sessionId ?? '').trim();
    }

    /**
     * Keys persisted on traveler_registers so Metrics can join web_visitor_geolocations.
     * Both must reference the same chat thread (thread_*).
     */
    cmsMetricsIdentity(): { sessionId: string; chatToken: string } {
        const sessionId = this.ensureSessionId();
        const chatToken = String(this.dataChat.token() ?? '').trim() || sessionId;
        return { sessionId, chatToken };
    }

    generateNewSessionId(){
        this.audioStorageService.clearAllAudios();
        this.sessionId = generateToken();
        this.setLocalStorageSessionIDFromClick();
        this.debugService.warn(`New SessionId generated: ${this.sessionId}`, '[ToolsService]');
        return this.sessionId;
    }
    
    setToken() {
        const token = this.tools.getWithExpiry("token");
        if (!token) 
          return;
    
        this.token = token;
        this.rest.token = token;
        this.debugService.warn(token, '[ToolsService] Token loaded from localStorage');
    }

    sumClicks(){
        this.clicks++;
    }

    addPoint(pointID){
        this.points.push(pointID.toString());
    }

    addEvent(eventID){
        this.events.push(eventID.toString());
    }
    
    addHiking(hikingID){
        this.hikings.push(hikingID.toString());
    }

    addPdf(pdfID){
        this.pdfs.push(pdfID.toString());
    }

    setInterest(interestArray){
        this.categories = interestArray;
    }

    setTravelerType(howTravel){
        this.travelerType = howTravel;
    }

    setSubInterest(subInterestArray){
        this.subCategories = subInterestArray;
    }

    setSesh(){
        const currentLang = this._translationService.displayLanguage();
        this.sesh = {
            clientId: this.rest.device()?.client,
            deviceId: 0,
            channelId: this.rest.device()?.channel ?? 0,
            deviceType: 0,
            sessionTime: this.sessionTime,
            clicks: this.clicks,
            points: this.points?.length > 0 ? this.points : null,
            events: this.events?.length > 0 ? this.events : null,
            hikings: this.hikings?.length > 0 ? this.hikings : null,
            pdfs: this.pdfs?.length > 0 ? this.pdfs : null,
            travelerType: this.travelerType,
            categories: this.categories,
            subCategories: this.subCategories,
            language: currentLang,
            login: this.login,
            subdomain: this.rest.subDomain,
            chatToken: this.dataChat.token(),
            sessionId: this.sessionId,
        };
    }

    /** Entrada web Cicerone: guarda geo ipapi en tabla dedicada. */
    sendWebEntry(geolocation: UserGeolocation): void {
        if (!this.rest.device()?.client)
            return;

        const currentLang = this._translationService.displayLanguage();
        this.rest.sendWebVisitorGeolocation({
            client_id: this.rest.device()?.client,
            chatbot_id: this.dataChat.id() || null,
            subdomain: this.rest.subDomain,
            session_id: this.sessionId,
            source: 'web_cicerone',
            language: currentLang,
            page_host: typeof window !== 'undefined' ? window.location.hostname : null,
            geolocation: {
                ip: geolocation.ip,
                city: geolocation.city,
                region: geolocation.region,
                region_code: geolocation.region_code,
                country: geolocation.country,
                country_code: geolocation.country_code,
                continent_code: geolocation.continent_code,
                timezone: geolocation.timezone,
                isp: geolocation.isp,
                latitude: geolocation.latitude,
                longitude: geolocation.longitude,
            },
        }).subscribe();
    }

    sendStats(){
        const currentTime = new Date().getTime();
        this.sessionActiveTime = this.sessionActiveTime + Math.round((currentTime - this.lastSessionTime) / 1000)
        this.sessionTime = this.sessionActiveTime
        this.setSesh();
        this.rest.sendStats(this.sesh).subscribe();
    }

}